import { c as _c } from "react/compiler-runtime";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { fetchMarkupSidePanelData, isSameAttachments } from "@pages/project-details/project-markups/sidepanel/fetch-markup-side-panel-data";
import { getMarkupByIdSelector } from "@store/markups/markups-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Loading state of the annotation */

/**
 * Handles closing the side panel.
 * Fetches and stores the data required to display the annotation side panel. An annotation
 * loaded before is shown at once and refreshed silently, and the next one is loaded ahead.
 */
export function useMarkupSidePanel(t0) {
  const $ = _c(48);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    nextMarkupId
  } = t1;
  const {
    selectedMarkup,
    updateSelectedMarkup,
    isMarkupUpdateInProgress,
    projectId,
    activeTab,
    updateActiveTab,
    updateIsSidePanelOpen,
    attachments,
    updateAttachments
  } = useMarkupContext();
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast
  } = useErrorContext();
  selectedMarkup?.id;
  const t2 = selectedMarkup?.id;
  let t3;
  if ($[2] !== t2) {
    t3 = getMarkupByIdSelector(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const markupByIdSelector = t3;
  const markup = useAppSelector(markupByIdSelector);
  let t4;
  if ($[4] !== projectId) {
    t4 = {
      projectId
    };
    $[4] = projectId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const projectApiClient = useProjectApiClient(t4);
  const [initializedId, setInitializedId] = useState();
  const [loadingState, setLoadingState] = useState("loading");
  const requestedIdRef = useRef(undefined);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Map();
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const [loadedAttachments, setLoadedAttachments] = useState(t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = new Map();
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const pendingRequestsRef = useRef(t6);
  let t7;
  if ($[8] !== isMarkupUpdateInProgress || $[9] !== updateActiveTab || $[10] !== updateIsSidePanelOpen || $[11] !== updateSelectedMarkup) {
    t7 = () => {
      if (isMarkupUpdateInProgress()) {
        return;
      }
      updateActiveTab("Info");
      updateIsSidePanelOpen(false);
      updateSelectedMarkup(undefined);
      setInitializedId(undefined);
      setLoadingState("loading");
    };
    $[8] = isMarkupUpdateInProgress;
    $[9] = updateActiveTab;
    $[10] = updateIsSidePanelOpen;
    $[11] = updateSelectedMarkup;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const closeSidePanel = t7;
  let t8;
  if ($[13] !== dispatch || $[14] !== projectApiClient) {
    t8 = markupId => {
      const pendingRequests = pendingRequestsRef.current;
      const pending = pendingRequests.get(markupId);
      if (pending) {
        return pending;
      }
      const request = dispatch(fetchMarkupSidePanelData({
        markupId,
        projectApiClient
      })).then(fetched => {
        setLoadedAttachments(previous => new Map(previous).set(markupId, fetched));
        return fetched;
      }).finally(() => pendingRequests.delete(markupId));
      pendingRequests.set(markupId, request);
      return request;
    };
    $[13] = dispatch;
    $[14] = projectApiClient;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const requestAnnotation = t8;
  let t10;
  let t9;
  if ($[16] !== activeTab || $[17] !== closeSidePanel || $[18] !== handleErrorWithToast || $[19] !== initializedId || $[20] !== loadedAttachments || $[21] !== requestAnnotation || $[22] !== selectedMarkup || $[23] !== updateAttachments) {
    t9 = () => {
      if (!selectedMarkup || selectedMarkup.id === initializedId) {
        return;
      }
      const markupId_0 = selectedMarkup.id;
      const cached = loadedAttachments.get(markupId_0);
      setInitializedId(markupId_0);
      requestedIdRef.current = markupId_0;
      if (cached) {
        updateAttachments(cached);
        setLoadingState("fetched");
      } else {
        setLoadingState("loading");
      }
      requestAnnotation(markupId_0).then(fetched_0 => {
        if (requestedIdRef.current !== markupId_0) {
          return;
        }
        if (!cached || !isSameAttachments(cached, fetched_0)) {
          updateAttachments(fetched_0);
        }
        setLoadingState(_temp);
      }).catch(error => {
        if (requestedIdRef.current !== markupId_0 || cached) {
          return;
        }
        closeSidePanel();
        handleErrorWithToast({
          id: `fetchAnnotation-${Date.now().toString()}`,
          title: activeTab === "Info" ? "Failed to open annotation details." : "Failed to open annotation attachments.",
          error
        });
      });
    };
    t10 = [activeTab, closeSidePanel, handleErrorWithToast, initializedId, loadedAttachments, requestAnnotation, selectedMarkup, updateAttachments];
    $[16] = activeTab;
    $[17] = closeSidePanel;
    $[18] = handleErrorWithToast;
    $[19] = initializedId;
    $[20] = loadedAttachments;
    $[21] = requestAnnotation;
    $[22] = selectedMarkup;
    $[23] = updateAttachments;
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[26] !== loadingState || $[27] !== markup) {
    t11 = () => {
      if (loadingState === "fetched" && markup) {
        setLoadingState("loaded");
      }
    };
    t12 = [loadingState, markup];
    $[26] = loadingState;
    $[27] = markup;
    $[28] = t11;
    $[29] = t12;
  } else {
    t11 = $[28];
    t12 = $[29];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[30] !== attachments || $[31] !== initializedId || $[32] !== loadingState) {
    t13 = () => {
      if (loadingState === "loaded" && initializedId) {
        setLoadedAttachments(previous_0 => previous_0.get(initializedId) === attachments ? previous_0 : new Map(previous_0).set(initializedId, attachments));
      }
    };
    t14 = [attachments, initializedId, loadingState];
    $[30] = attachments;
    $[31] = initializedId;
    $[32] = loadingState;
    $[33] = t13;
    $[34] = t14;
  } else {
    t13 = $[33];
    t14 = $[34];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[35] !== loadedAttachments || $[36] !== loadingState || $[37] !== nextMarkupId || $[38] !== requestAnnotation) {
    t15 = () => {
      if (loadingState !== "loaded" || !nextMarkupId || loadedAttachments.has(nextMarkupId)) {
        return;
      }
      requestAnnotation(nextMarkupId).catch(_temp2);
    };
    t16 = [loadedAttachments, loadingState, nextMarkupId, requestAnnotation];
    $[35] = loadedAttachments;
    $[36] = loadingState;
    $[37] = nextMarkupId;
    $[38] = requestAnnotation;
    $[39] = t15;
    $[40] = t16;
  } else {
    t15 = $[39];
    t16 = $[40];
  }
  useEffect(t15, t16);
  const t17 = loadingState !== "loaded" || initializedId !== selectedMarkup?.id;
  let t18;
  if ($[41] !== loadedAttachments || $[42] !== nextMarkupId) {
    t18 = !!nextMarkupId && loadedAttachments.has(nextMarkupId);
    $[41] = loadedAttachments;
    $[42] = nextMarkupId;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== closeSidePanel || $[45] !== t17 || $[46] !== t18) {
    t19 = {
      isLoading: t17,
      isNextMarkupLoaded: t18,
      closeSidePanel
    };
    $[44] = closeSidePanel;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  return t19;
}
function _temp2() {}
function _temp(state) {
  return state === "loading" ? "fetched" : state;
}